import { createEkSlider, getEkSlider } from "./ekSwiperSlider";

declare global {
  interface Window {
    Swiper: any;
  }
}

interface CommunityPostSwiperInstance {
  update: () => void;
  destroy: (deleteInstance?: boolean, cleanStyles?: boolean) => void;
}

interface PreviewSwiperInstance extends CommunityPostSwiperInstance {
  activeIndex: number;
  isBeginning: boolean;
  isEnd: boolean;
  isLocked: boolean;
  slides: HTMLElement[];
  slideTo: (index: number) => void;
  setTranslate: (translate: number) => void;
  on: (event: string, handler: () => void) => void;
  off: (event: string, handler: () => void) => void;
}

const MODAL_OPEN_CLASSES = ["pointer-events-auto", "opacity-100"] as const;
const MODAL_CLOSED_CLASSES = ["pointer-events-none", "opacity-0"] as const;
const PANEL_OPEN_CLASSES = ["opacity-100", "translate-y-0"] as const;
const PANEL_CLOSED_CLASSES = ["opacity-0", "translate-y-4"] as const;
const BODY_MODAL_OPEN_CLASS = "community-post-modal-open";

const modalSwiperInstances = new Map<string, CommunityPostSwiperInstance>();
const previewSwiperInstances = new Map<string, PreviewSwiperInstance>();

interface PreviewPlaylist {
  videos: HTMLVideoElement[];
  currentIndex: number;
  isActive: boolean;
}

const previewPlaylists = new Map<string, PreviewPlaylist>();

// WebKit can interrupt an active touch gesture when videos start/stop nearby,
// leaving Swiper's document-level touchmove handler in a stuck state that
// preventDefault()s all native scrolling on the page. Two layers of defense:
// 1. never touch video playback state while a finger is on the preview swiper,
// 2. reset stuck Swiper touch state once all fingers have left the screen.
let previewGestureActive = false;
let pendingPlaylistAction: (() => void) | null = null;
let touchSequence = 0;

function resetStuckSwiperTouchState(event: TouchEvent): void {
  if (event.touches.length > 0) return;

  // Defer past Swiper's own (earlier-registered) document handlers so a healthy
  // gesture is fully processed first; skip if a new touch began in the meantime.
  const sequenceAtRelease = touchSequence;
  setTimeout(() => {
    if (sequenceAtRelease !== touchSequence) return;

    previewGestureActive = false;
    const action = pendingPlaylistAction;
    pendingPlaylistAction = null;
    action?.();

    for (const instance of [...previewSwiperInstances.values(), ...modalSwiperInstances.values()]) {
      const data = (instance as any).touchEventsData;
      if (!data) continue;
      data.isTouched = false;
      data.isMoved = false;
      data.startMoving = false;
      data.touchId = null;
      data.preventTouchMoveFromPointerMove = false;
    }
  }, 0);
}

function installStuckTouchStateSafetyNet(): void {
  if (document.documentElement.dataset.communityPostsTouchResetInit === "true") return;
  document.documentElement.dataset.communityPostsTouchResetInit = "true";
  document.addEventListener(
    "touchstart",
    () => {
      touchSequence += 1;
    },
    { passive: true },
  );
  document.addEventListener("touchend", resetStuckSwiperTouchState, { passive: true });
  document.addEventListener("touchcancel", resetStuckSwiperTouchState, { passive: true });
}

function isCommunityPostModalOpen(root: ParentNode = document): boolean {
  return Array.from(root.querySelectorAll<HTMLElement>("[data-community-post-modal]")).some(
    (modal) => !modal.hidden && modal.classList.contains("opacity-100"),
  );
}

function playCommunityPostVideo(video: HTMLVideoElement): void {
  video.muted = true;
  void video.play().catch(() => undefined);
}

function pauseCommunityPostModalVideos(modalId: string, root: ParentNode = document): void {
  const modal = root.querySelector<HTMLElement>(`[data-community-post-modal][data-post-modal-id="${modalId}"]`);
  if (!modal) return;

  for (const video of Array.from(modal.querySelectorAll<HTMLVideoElement>("[data-community-post-video]"))) {
    video.pause();
  }
}

function syncCommunityPostModalVideos(modalId: string, root: ParentNode = document): void {
  const modal = root.querySelector<HTMLElement>(`[data-community-post-modal][data-post-modal-id="${modalId}"]`);
  if (!modal) return;

  for (const video of Array.from(modal.querySelectorAll<HTMLVideoElement>("[data-community-post-video]"))) {
    const slide = video.closest(".swiper-slide");
    const shouldPlay = slide?.classList.contains("swiper-slide-active") ?? modal.querySelectorAll("[data-community-post-video]").length === 1;

    if (shouldPlay) playCommunityPostVideo(video);
    else video.pause();
  }
}

function pauseAllPreviewCarouselVideos(root: ParentNode = document): void {
  for (const playlist of previewPlaylists.values()) {
    playlist.isActive = false;
    for (const video of playlist.videos) {
      video.pause();
    }
  }

  for (const video of Array.from(root.querySelectorAll<HTMLVideoElement>("[data-community-post-video-preview]"))) {
    video.pause();
  }
}

function isElementVisibleInViewport(element: Element): boolean {
  const rect = element.getBoundingClientRect();
  return rect.top < window.innerHeight && rect.bottom > 0 && rect.width > 0;
}

function playPreviewVideoAt(playlist: PreviewPlaylist, index: number, resetTime = true): void {
  if (playlist.videos.length === 0 || isCommunityPostModalOpen()) return;

  if (previewGestureActive) {
    pendingPlaylistAction = () => playPreviewVideoAt(playlist, index, resetTime);
    return;
  }

  const normalizedIndex =
    ((index % playlist.videos.length) + playlist.videos.length) % playlist.videos.length;
  playlist.currentIndex = normalizedIndex;

  for (const video of playlist.videos) {
    video.pause();
  }

  const video = playlist.videos[normalizedIndex];
  if (resetTime) video.currentTime = 0;
  playCommunityPostVideo(video);
}

function advancePreviewPlaylist(playlist: PreviewPlaylist): void {
  if (!playlist.isActive || isCommunityPostModalOpen()) return;
  playPreviewVideoAt(playlist, playlist.currentIndex + 1, true);
}

function initPreviewPlaylist(sectionEl: HTMLElement, sectionId: string, root: ParentNode): void {
  if (sectionEl.dataset.previewPlaylistInit === "true") return;

  const videos = Array.from(
    sectionEl.querySelectorAll<HTMLVideoElement>("[data-community-post-video-preview]"),
  );
  if (videos.length === 0) return;

  sectionEl.dataset.previewPlaylistInit = "true";

  const playlist: PreviewPlaylist = {
    videos,
    currentIndex: 0,
    isActive: false,
  };
  previewPlaylists.set(sectionId, playlist);

  for (const video of videos) {
    if (video.dataset.communityPostVideoInit !== "true") {
      video.dataset.communityPostVideoInit = "true";
      video.muted = true;
      video.loop = false;
      video.removeAttribute("autoplay");
      video.removeAttribute("loop");

      video.addEventListener("ended", () => {
        advancePreviewPlaylist(playlist);
      });
    }
  }

  const section = sectionEl.closest("[data-community-posts]") ?? sectionEl;
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.2) {
          if (isCommunityPostModalOpen()) {
            playlist.isActive = false;
            for (const video of playlist.videos) {
              video.pause();
            }
            return;
          }

          playlist.isActive = true;
          playPreviewVideoAt(playlist, playlist.currentIndex, false);
          return;
        }

        playlist.isActive = false;
        for (const video of playlist.videos) {
          video.pause();
        }
      }
    },
    { threshold: [0, 0.2, 0.5] },
  );

  observer.observe(section);

  if (isElementVisibleInViewport(section) && !isCommunityPostModalOpen(root)) {
    playlist.isActive = true;
    playPreviewVideoAt(playlist, 0, true);
  }
}

function destroyCommunityPostPreviewSwiper(sectionId: string, root: ParentNode = document): void {
  const ekInstance = getEkSlider(sectionId);
  if (ekInstance) {
    ekInstance.destroy();
  } else {
    try {
      previewSwiperInstances.get(sectionId)?.destroy(true, true);
    } catch {}
  }
  previewSwiperInstances.delete(sectionId);

  previewPlaylists.delete(sectionId);

  const slider = root.querySelector<HTMLElement>(`[data-community-posts-slider][data-section-id="${sectionId}"]`);
  if (slider) {
    delete slider.dataset.previewSwiperInit;
    delete slider.dataset.previewPlaylistInit;
  }

  const swiperEl = root.querySelector<HTMLElement>(
    `[data-community-posts-preview-swiper][data-section-id="${sectionId}"]`,
  );
  if (swiperEl) {
    delete swiperEl.dataset.previewSwiperInit;
    swiperEl.classList.remove("is-preview-locked");
  }
}

function resolvePreviewLockTranslate(swiper: PreviewSwiperInstance, swiperEl: HTMLElement): number {
  const slideCount = Number.parseInt(swiperEl.dataset.slideCount || "0", 10);
  const isMobile = !window.matchMedia("(min-width: 768px)").matches;

  if (isMobile && slideCount === 1) {
    const slide = swiper.slides[0] as HTMLElement | undefined;
    if (!slide) return swiper.minTranslate();
    return Math.max(0, (swiper.width - slide.offsetWidth) / 2);
  }

  return swiper.minTranslate();
}

function alignLockedPreviewSwiper(swiper: PreviewSwiperInstance, swiperEl: HTMLElement): void {
  swiperEl.classList.toggle("is-preview-locked", swiper.isLocked);
  if (!swiper.isLocked) return;
  swiper.setTranslate(resolvePreviewLockTranslate(swiper, swiperEl));
}

function initCommunityPostsPreviewSwipers(root: ParentNode = document): void {
  if (typeof window.Swiper !== "function") {
    setTimeout(() => initCommunityPostsPreviewSwipers(root), 50);
    return;
  }

  for (const sectionEl of Array.from(root.querySelectorAll<HTMLElement>("[data-community-posts-slider]"))) {
    const sectionId = sectionEl.dataset.sectionId;
    if (!sectionId || sectionEl.dataset.previewSwiperInit === "true") continue;

    const swiperEl = sectionEl.querySelector<HTMLElement>("[data-community-posts-preview-swiper]");
    if (!swiperEl) continue;

    sectionEl.dataset.previewSwiperInit = "true";
    swiperEl.dataset.previewSwiperInit = "true";

    const slideCount = Number.parseInt(swiperEl.dataset.slideCount || "0", 10);
    const hasMultipleSlides = slideCount > 1;

    const created = createEkSlider({
      sectionEl,
      sectionId,
      swiperEl,
      hasProductCards: false,
      arrowRules: { left: "md-only", right: "always" },
      resolveLockTranslate: (swiper) => resolvePreviewLockTranslate(swiper, swiperEl),
      swiperOverrides: {
        spaceBetween: 16,
        centeredSlides: !hasMultipleSlides,
        slidesOffsetBefore: hasMultipleSlides ? 20 : 0,
        slidesOffsetAfter: hasMultipleSlides ? 20 : 0,
        breakpoints: {
          768: {
            spaceBetween: 16,
            centeredSlides: false,
            slidesOffsetBefore: 0,
            slidesOffsetAfter: 0,
          },
        },
        on: {
          init: (swiper: PreviewSwiperInstance) => {
            previewSwiperInstances.set(sectionId, swiper);
            alignLockedPreviewSwiper(swiper, swiperEl);
            initPreviewPlaylist(sectionEl, sectionId, root);
            requestAnimationFrame(() => {
              if (swiper.destroyed) return;
              alignLockedPreviewSwiper(swiper, swiperEl);
            });
          },
          touchStart: () => {
            previewGestureActive = true;
          },
          touchEnd: () => {
            previewGestureActive = false;
            const action = pendingPlaylistAction;
            pendingPlaylistAction = null;
            action?.();
          },
          lock: (swiper: PreviewSwiperInstance) => {
            alignLockedPreviewSwiper(swiper, swiperEl);
          },
          unlock: (swiper: PreviewSwiperInstance) => {
            alignLockedPreviewSwiper(swiper, swiperEl);
          },
          resize: (swiper: PreviewSwiperInstance) => {
            alignLockedPreviewSwiper(swiper, swiperEl);
          },
        },
      },
    });

    if (created && !previewSwiperInstances.has(sectionId)) {
      previewSwiperInstances.set(sectionId, created.swiper as PreviewSwiperInstance);
    }
  }
}

function resumeVisiblePreviewPlaylists(root: ParentNode = document): void {
  if (isCommunityPostModalOpen(root)) return;

  for (const slider of Array.from(root.querySelectorAll<HTMLElement>("[data-community-posts-slider]"))) {
    const sectionId = slider.dataset.sectionId;
    if (!sectionId) continue;

    const playlist = previewPlaylists.get(sectionId);
    if (!playlist || playlist.videos.length === 0) continue;

    const section = slider.closest("[data-community-posts]");
    if (!section || !isElementVisibleInViewport(section)) continue;

    playlist.isActive = true;
    playPreviewVideoAt(playlist, playlist.currentIndex, false);
  }
}

function syncBodyModalState(): void {
  const anyOpen = Array.from(document.querySelectorAll<HTMLElement>("[data-community-post-modal]")).some((modal) => !modal.hidden);
  document.body.classList.toggle(BODY_MODAL_OPEN_CLASS, anyOpen);
}

function queryModalIds(root: ParentNode): Set<string> {
  const ids = new Set<string>();
  for (const el of Array.from(root.querySelectorAll<HTMLElement>("[data-community-post-modal]"))) {
    const id = el.dataset.postModalId;
    if (id) ids.add(id);
  }
  return ids;
}

function getModalElements(root: ParentNode, modalId: string) {
  const modal = root.querySelector<HTMLElement>(`[data-community-post-modal][data-post-modal-id="${modalId}"]`);
  const panel = root.querySelector<HTMLElement>(`[data-community-post-panel][data-post-modal-id="${modalId}"]`);
  const backdrop = root.querySelector<HTMLElement>(`[data-community-post-backdrop][data-post-modal-id="${modalId}"]`);
  return { modal, panel, backdrop };
}

function setModalOpenState(
  modal: HTMLElement,
  panel: HTMLElement | null,
  backdrop: HTMLElement | null,
  isOpen: boolean,
): void {
  if (isOpen) {
    modal.hidden = false;
    modal.removeAttribute("aria-hidden");
    // Leaving display:none ([hidden]) — flush layout so the opacity/transform transition still runs
    void modal.offsetWidth;
    modal.classList.remove(...MODAL_CLOSED_CLASSES);
    modal.classList.add(...MODAL_OPEN_CLASSES);
    backdrop?.classList.remove("opacity-0");
    backdrop?.classList.add("opacity-100");
    panel?.classList.remove(...PANEL_CLOSED_CLASSES);
    panel?.classList.add(...PANEL_OPEN_CLASSES);
    document.body.style.overflow = "hidden";
    pauseAllPreviewCarouselVideos(document);
    syncBodyModalState();
    return;
  }

  modal.classList.remove(...MODAL_OPEN_CLASSES);
  modal.classList.add(...MODAL_CLOSED_CLASSES);
  modal.setAttribute("aria-hidden", "true");
  backdrop?.classList.remove("opacity-100");
  backdrop?.classList.add("opacity-0");
  panel?.classList.remove(...PANEL_OPEN_CLASSES);
  panel?.classList.add(...PANEL_CLOSED_CLASSES);
  document.body.style.overflow = "";
  syncBodyModalState();

  panel?.addEventListener(
    "transitionend",
    () => {
      if (modal.classList.contains("opacity-0")) {
        modal.hidden = true;
        syncBodyModalState();
        if (!isCommunityPostModalOpen(document)) {
          resumeVisiblePreviewPlaylists(document);
        }
      }
    },
    { once: true },
  );
}

function destroyCommunityPostModalSwiper(modalId: string, root: ParentNode = document): void {
  const existing = modalSwiperInstances.get(modalId);
  if (existing) {
    existing.destroy(true, true);
    modalSwiperInstances.delete(modalId);
  }

  const swiperEl = root.querySelector<HTMLElement>(
    `[data-community-post-modal-swiper][data-post-modal-id="${modalId}"]`,
  );
  if (swiperEl) {
    delete swiperEl.dataset.swiperInit;
  }
}

function destroyCommunityPostModalSwipersInRoot(root: ParentNode): void {
  for (const modal of Array.from(root.querySelectorAll<HTMLElement>("[data-community-post-modal]"))) {
    const modalId = modal.dataset.postModalId;
    if (modalId) destroyCommunityPostModalSwiper(modalId, root);
  }
}

function resetCommunityPostModalScroll(modalId: string, root: ParentNode = document): void {
  const scrollEl = root.querySelector<HTMLElement>(
    `[data-community-post-modal-scroll][data-post-modal-id="${modalId}"]`,
  );
  if (scrollEl) scrollEl.scrollTop = 0;
}

function initCommunityPostModalSwiper(modalId: string, root: ParentNode = document): void {
  const swiperEl = root.querySelector<HTMLElement>(
    `[data-community-post-modal-swiper][data-post-modal-id="${modalId}"]`,
  );
  if (!swiperEl || swiperEl.dataset.swiperInit === "true") return;

  const slideCount = Number.parseInt(swiperEl.dataset.slideCount || "0", 10);
  if (slideCount <= 1) return;

  if (typeof window.Swiper !== "function") {
    setTimeout(() => initCommunityPostModalSwiper(modalId, root), 50);
    return;
  }

  swiperEl.dataset.swiperInit = "true";

  const swiperInstance = new window.Swiper(swiperEl, {
    slidesPerView: 1,
    loop: false,
    nested: true,
    touchReleaseOnEdges: true,
    touchEventsTarget: "container",
    navigation: {
      nextEl: swiperEl.querySelector(".community-post-modal-swiper-next"),
      prevEl: swiperEl.querySelector(".community-post-modal-swiper-prev"),
    },
    pagination: {
      el: swiperEl.querySelector(".community-post-modal-swiper-pagination"),
      clickable: true,
    },
    on: {
      slideChange: () => {
        syncCommunityPostModalVideos(modalId, root);
      },
      resize: function (this: { update: () => void }) {
        this.update();
      },
    },
  });

  modalSwiperInstances.set(modalId, swiperInstance);
  syncCommunityPostModalVideos(modalId, root);
}

function updateCommunityPostModalSwiper(modalId: string, root: ParentNode = document): void {
  const existing = modalSwiperInstances.get(modalId);
  if (existing) {
    existing.update();
    syncCommunityPostModalVideos(modalId, root);
    return;
  }

  initCommunityPostModalSwiper(modalId, root);
}

function closeCommunityPostModal(root: ParentNode, modalId: string): void {
  const { modal, panel, backdrop } = getModalElements(root, modalId);
  if (!modal) return;
  pauseCommunityPostModalVideos(modalId, root);
  destroyCommunityPostModalSwiper(modalId, root);
  setModalOpenState(modal, panel, backdrop, false);
}

function closeAllCommunityPostModalsExcept(root: ParentNode, exceptModalId: string): void {
  for (const modalId of queryModalIds(root)) {
    if (modalId === exceptModalId) continue;
    closeCommunityPostModal(root, modalId);
  }
}

function openCommunityPostModal(root: ParentNode, modalId: string): void {
  const { modal, panel, backdrop } = getModalElements(root, modalId);
  if (!modal) return;

  pauseAllPreviewCarouselVideos(root);

  closeAllCommunityPostModalsExcept(root, modalId);
  setModalOpenState(modal, panel, backdrop, true);
  resetCommunityPostModalScroll(modalId, root);
  updateCommunityPostModalSwiper(modalId, root);
  syncCommunityPostModalVideos(modalId, root);
}

function bindCommunityPostModalControls(
  root: ParentNode,
  selector: string,
  handler: (modalId: string) => void,
): void {
  for (const element of Array.from(root.querySelectorAll<HTMLElement>(selector))) {
    const modalId = element.dataset.postModalId;
    if (!modalId || element.dataset.communityPostInit === "true") continue;
    element.dataset.communityPostInit = "true";
    element.addEventListener("click", () => handler(modalId));
  }
}

function initCommunityPostModals(root: ParentNode = document): void {
  bindCommunityPostModalControls(root, "[data-community-post-trigger]", (modalId) => {
    openCommunityPostModal(document, modalId);
  });
  bindCommunityPostModalControls(root, "[data-community-post-close]", (modalId) => {
    closeCommunityPostModal(document, modalId);
  });
  bindCommunityPostModalControls(root, "[data-community-post-backdrop]", (modalId) => {
    closeCommunityPostModal(document, modalId);
  });

  if (root === document && !document.documentElement.dataset.communityPostsEscapeInit) {
    document.documentElement.dataset.communityPostsEscapeInit = "true";
    document.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;

      for (const modal of Array.from(document.querySelectorAll<HTMLElement>("[data-community-post-modal]:not([hidden])"))) {
        const modalId = modal.dataset.postModalId;
        if (!modalId || modal.classList.contains("opacity-0")) continue;
        closeCommunityPostModal(document, modalId);
        break;
      }
    });
  }
}

export function initEkCommunityPosts(root: ParentNode = document): void {
  installStuckTouchStateSafetyNet();
  initCommunityPostsPreviewSwipers(root);
  initCommunityPostModals(root);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => initEkCommunityPosts());
} else {
  initEkCommunityPosts();
}

document.addEventListener("shopify:section:load", (event) => {
  const target = (event.target as ParentNode) || document;
  initEkCommunityPosts(target);
});

document.addEventListener("shopify:section:unload", (event) => {
  const target = (event.target as ParentNode) || document;
  destroyCommunityPostModalSwipersInRoot(target);

  for (const slider of Array.from(target.querySelectorAll<HTMLElement>("[data-community-posts-slider]"))) {
    const sectionId = slider.dataset.sectionId;
    if (sectionId) destroyCommunityPostPreviewSwiper(sectionId, target);
  }
});
